import React, { useState, useEffect } from "react";
import { 
  WifiIcon, 
  CheckCircleIcon,
  ChartBarIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";
import { useDarkMode } from "../darkmode/DarkModeContext";
import { useAuth } from "../../pages/auth/AuthContext";

// API Configuration
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

const Status = ({ packages: propPackages }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isDarkMode } = useDarkMode(); 
  const { user, token } = useAuth();

  // Better token and user handling
  const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token');
  const currentUser = user?.user || user?.profile || user || null;
  const userId = currentUser?._id || currentUser?.id || currentUser?.userId || null;

  // Format currency helper
  const formatCurrency = (num) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  // Transform orders to packages for statistics
  const transformOrdersToPackages = (orders) => {
    return orders.map((order, index) => {
      const packageInfo = order.packageSnapshot || order.package || {};
      
      // Extract price
      let monthlyFee = 0;
      if (packageInfo.price?.amount) {
        monthlyFee = packageInfo.price.amount;
      } else if (order.pricing?.packagePrice) {
        monthlyFee = order.pricing.packagePrice;
      } else if (order.pricing?.totalAmount) {
        monthlyFee = order.pricing.totalAmount;
      }

      // Map status
      const statusMap = {
        'pending': 'pending',
        'confirmed': 'pending',
        'survey_scheduled': 'pending',
        'survey_completed': 'pending',
        'installation_scheduled': 'pending',
        'installed': 'aktif',
        'active': 'aktif',
        'suspended': 'suspend',
        'cancelled': 'expired'
      };

      const status = statusMap[order.status] || 'pending';
      const uptime = (status === 'aktif') ? 99.5 + Math.random() * 0.4 : 0;

      return {
        id: order._id || order.id || `order_${index}`,
        name: packageInfo.name || `Paket ${packageInfo.speed || 'Internet'}`,
        status: status,
        monthlyFee: monthlyFee,
        uptime: uptime,
        orderNumber: order.orderNumber,
        createdAt: order.createdAt
      };
    });
  };

  // Fetch packages data from API
  const fetchPackagesData = async () => {
    setLoading(true);
    setError(null);

    try {
      let transformedPackages = [];
      let response;

      console.log('Status component: Fetching data for user:', {
        userId,
        userPhone: currentUser?.phone,
        userEmail: currentUser?.email,
        hasToken: !!authToken
      });

      if (authToken && userId) {
        // Try authenticated endpoint first
        try {
          response = await fetch(`${API_BASE_URL}/orders/my-orders`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            credentials: 'include'
          });

          if (response.ok) {
            const result = await response.json();
            // console.log('Status component - My-orders response:', result);
            
            if (result.success && result.data && result.data.length > 0) {
              transformedPackages = transformOrdersToPackages(result.data);
              // console.log('Status component - Transformed packages:', transformedPackages);
            }
          }
        } catch (authError) {
          console.error('Status component - Authenticated request failed:', authError);
        }
      }

      // Try customer endpoint if no data from authenticated endpoint
      if (transformedPackages.length === 0 && (currentUser?.phone || currentUser?.email)) {
        try {
          const queryParams = new URLSearchParams();
          if (currentUser.phone) queryParams.append('phone', currentUser.phone);
          if (currentUser.email) queryParams.append('email', currentUser.email);
          
          const customerUrl = `${API_BASE_URL}/orders/customer?${queryParams.toString()}`;
          // console.log('Status component - Trying customer endpoint:', customerUrl);

          response = await fetch(customerUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const result = await response.json();
            // console.log('Status component - Customer orders response:', result);
            
            if (result.success && result.data && result.data.length > 0) {
              transformedPackages = transformOrdersToPackages(result.data);
            }
          }
        } catch (customerError) {
          // console.error('Status component - Customer endpoint failed:', customerError);
        }
      }

      // Use fallback data if no real data found
      // if (transformedPackages.length === 0) {
      //   console.log('Status component - No data found, using mock data');
      //   transformedPackages = getMockPackages();
      //   setError('Tidak ada data order ditemukan. Menampilkan data demo.');
      // }

      setPackages(transformedPackages);

    } catch (error) {
      console.error('Status component - Error fetching packages:', error);
      setError('Gagal mengambil data paket. Menampilkan data demo.');
      setPackages(getMockPackages());
    } finally {
      setLoading(false);
    }
  };

  // Mock data fallback
  const getMockPackages = () => [
    {
      id: 'mock_1',
      name: "Internet Fiber 100 Mbps",
      status: "aktif",
      monthlyFee: 450000,
      uptime: 99.8,
      orderNumber: 'DEMO-001'
    },
    {
      id: 'mock_2',
      name: "Internet Dedicated 50 Mbps",
      status: "aktif",
      monthlyFee: 2500000,
      uptime: 99.9,
      orderNumber: 'DEMO-002'
    },
    {
      id: 'mock_3',
      name: "Internet Broadband 25 Mbps",
      status: "suspend",
      monthlyFee: 299000,
      uptime: 0,
      orderNumber: 'DEMO-003'
    },
    {
      id: 'mock_4',
      name: "Internet Satelit Starlink",
      status: "pending",
      monthlyFee: 1200000,
      uptime: 0,
      orderNumber: 'DEMO-004'
    }
  ];

  // Effect to fetch data
  useEffect(() => {
    // If packages are passed as props, use them instead of fetching
    if (propPackages && propPackages.length > 0) {
      // console.log('Status component - Using packages from props:', propPackages);
      setPackages(propPackages);
      setLoading(false);
    } else {
      // Otherwise fetch from API
      fetchPackagesData();
    }
  }, [propPackages, user, authToken]);

  // Refresh function
  const refreshData = async () => {
    await fetchPackagesData();
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-6 animate-pulse`}>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className={`h-4 w-20 ${isDarkMode ? "bg-gray-700" : "bg-gray-300"} rounded`}></div>
                <div className={`h-8 w-16 ${isDarkMode ? "bg-gray-700" : "bg-gray-300"} rounded`}></div>
              </div>
              <div className={`w-12 h-12 ${isDarkMode ? "bg-gray-700" : "bg-gray-300"} rounded-lg`}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const aktifPackages = packages.filter(p => p.status === "aktif");
  const suspendPackages = packages.filter(p => p.status === "suspend");
  const pendingPackages = packages.filter(p => p.status === "pending");
  const totalBiaya = aktifPackages.reduce((sum, p) => sum + (p.monthlyFee || 0), 0);
  const rataUptime = aktifPackages.length > 0
    ? (aktifPackages.reduce((sum, p) => sum + (p.uptime || 0), 0) / aktifPackages.length).toFixed(1)
    : 0;

  return (
    <div className="mb-8">
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
              <span className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                {error}
              </span>
            </div>
            <button 
              onClick={refreshData}
              className="p-1 hover:bg-yellow-500/30 rounded"
              title="Refresh data"
            >
              <ArrowPathIcon className="h-4 w-4 text-yellow-500" />
            </button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Paket */}
        <div className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-6 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Total Paket</p>
              <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{packages.length}</p>
              <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Semua status
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <WifiIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Paket Aktif */}
        <div className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-6 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Paket Aktif</p>
              <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{aktifPackages.length}</p>
              <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                {suspendPackages.length} suspend, {pendingPackages.length} pending
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Total Biaya/Bulan */}
        <div className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-6 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Total Biaya/Bulan</p>
              <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                {totalBiaya > 0 ? formatCurrency(totalBiaya) : 'Rp 0'}
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Hanya paket aktif
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Rata-rata Uptime */}
        <div className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-6 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Rata-rata Uptime</p>
              <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                {rataUptime}%
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                {aktifPackages.length} paket aktif
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Status;